/* eslint-disable camelcase */
import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { cioFacetsToUrlFilters } from '~/grid/utils/cioUtils';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { CUSTOM_COLOR_FILTER } from '../../constants';
import useSelectedFacetsToApply from '../../hooks/useSelectedFacetsToApply';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  getSelectedOptionCount,
  isCioColorFacet,
  isProductsV2ColorFacet,
  sanitizeFilterName,
} from '../../utils/filters';
import AnimatedDisclosurePanel from '../SharedGridFiltersUi/AnimatedDisclosurePanel';
import BrandedToggleIcon from '../SharedGridFiltersUi/BrandedToggleIcon';

const DynamicFormattedMessage = FormattedMessage;

const MobileGridFilterGroupDisclosureContainer = styled.div`
  font-size: 0.875rem;
`;

const MobileGridFilterGroupDisclosureToggleButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1.5rem;
  border: none;
  color: ${({ theme }) =>
    theme.colors.gridFilterGroupDisclosureToggleButtonTextColor};
  background: none;
  cursor: pointer;
  font-family: inherit;

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) =>
        theme.colors
          .gridFilterGroupDisclosureToggleButtonFocusVisibleOutlineColor};
    outline-offset: 2px;
  }
  ${({ theme }) => theme.gridFilterStyles.filterCategoryTitleStyle};
`;

const SelectedCount = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) =>
    theme.colors.gridFilterGroupDisclosurePanelWrapperSelectedCountTextColor};
`;

function MobileGridFilterGroupDisclosure({
  display_name,
  name,
  sub_facets,
  options = [],
  children,
  togglePanel,
  openPanels,
}) {
  const sanitizedFacetName = slugify(name, '-');
  const panelId = `mobile-disclosure-panel-${sanitizedFacetName}`;
  const sanitizedName = sanitizeFilterName(name);
  const isOpen = openPanels[panelId] || false;
  const { selectedFacetsToApply } = useSelectedFacetsToApply();
  const { customFacetFilters } = useUnifiedProductGridData();

  const selectedUrlFiltersToApply = Object.entries(
    selectedFacetsToApply
  ).reduce((acc, [key, value]) => {
    const mappedKey = cioFacetsToUrlFilters[key] || key;

    return {
      ...acc,
      [mappedKey]: value,
    };
  }, {});

  const numSelected = getSelectedOptionCount({
    name,
    subFacets: sub_facets,
    options,
    selectedUrlFilters: selectedUrlFiltersToApply,
  });

  const isColorFacet = isCioColorFacet(name) || isProductsV2ColorFacet(name);
  const pendingCustomColor = selectedFacetsToApply[CUSTOM_COLOR_FILTER.name];
  const hasPendingCustomColor = !!pendingCustomColor?.length;
  const customColorExplicitlyCleared =
    CUSTOM_COLOR_FILTER.name in selectedFacetsToApply && !hasPendingCustomColor;
  const appliedCustomColor = customFacetFilters[CUSTOM_COLOR_FILTER.name];
  const hasCustomColorSelected =
    isColorFacet &&
    (hasPendingCustomColor ||
      (!customColorExplicitlyCleared &&
        !!appliedCustomColor &&
        !appliedCustomColor.hasError));
  const effectiveNumSelected = numSelected + (hasCustomColorSelected ? 1 : 0);

  return (
    <MobileGridFilterGroupDisclosureContainer>
      <MobileGridFilterGroupDisclosureToggleButton
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => togglePanel(panelId)}
        data-autotag={`filter-${sanitizedName}`}
      >
        <div>
          <DynamicFormattedMessage
            id={`site_product_filter.${slugify(
              getBrandValue({
                savage: name,
                default: display_name,
              })
            )}`}
            defaultMessage={display_name}
          />
          {effectiveNumSelected > 0 && (
            <SelectedCount> ({effectiveNumSelected})</SelectedCount>
          )}
        </div>
        <BrandedToggleIcon open={isOpen} name={sanitizedName} />
      </MobileGridFilterGroupDisclosureToggleButton>

      <AnimatedDisclosurePanel
        open={isOpen}
        panelId={panelId}
        viewport="mobile"
      >
        {children}
      </AnimatedDisclosurePanel>
    </MobileGridFilterGroupDisclosureContainer>
  );
}

MobileGridFilterGroupDisclosure.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  display_name: PropTypes.string.isRequired,
  name: PropTypes.string,
  openPanels: PropTypes.objectOf(PropTypes.bool).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  sub_facets: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      name: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          display_name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
    })
  ),
  togglePanel: PropTypes.func.isRequired,
};

export default MobileGridFilterGroupDisclosure;
